// Fatos do cabeçalho do cartão de controle (D-01, D-03, D-04).
// Tudo deriva da edição imutável e do histórico real: nenhum valor vem do estado atual da OP.
import type { CartaoControleEdicaoView } from '@pieltec/contracts';
import { formatarDataHoraOperacional } from '../data-hora-operacional';

export const AVISO_RASCUNHO_CP = 'Rascunho — a revisão ainda pode mudar antes da liberação';

export type SituacaoCp = 'Rascunho' | 'Liberado';

export interface FatosCabecalhoCp {
  readonly identificador: string;
  readonly situacao: SituacaoCp;
  readonly avisoRascunho: string | null;
  readonly dataHora: string;
  /** Sequência da edição que substitui esta; null quando esta edição não foi substituída. */
  readonly substituidaPor: number | null;
}

export function identificadorCp(sequencia: number): string {
  return `CP-${sequencia}`;
}

/**
 * Rascunho quando o status da OP no snapshot é nulo ou RASCUNHO; qualquer status pós-liberação é Liberado.
 * OP CANCELADA só é Liberado se o snapshot registrou o roteiro como fixado (a API só fixa quem passou pela
 * liberação); cancelada sem nunca ter sido liberada continua Rascunho (WR-06).
 */
export function situacaoDaEdicao(edicao: Pick<CartaoControleEdicaoView, 'snapshot'>): SituacaoCp {
  const { op, roteiro } = edicao.snapshot;
  if (op.status === null || op.status === 'RASCUNHO') return 'Rascunho';
  if (op.status === 'CANCELADA' && roteiro.estado !== 'FIXADO') return 'Rascunho';
  return 'Liberado';
}

/** Menor sequência entre as edições que apontam para esta em reimpressaoDeId. */
export function substituidaPorSequencia(edicao: Pick<CartaoControleEdicaoView, 'id'>, historico: readonly CartaoControleEdicaoView[]): number | null {
  const candidatas = historico.filter((outra) => outra.reimpressaoDeId === edicao.id).map((outra) => outra.sequencia);
  return candidatas.length > 0 ? Math.min(...candidatas) : null;
}

export function fatosCabecalhoCp(edicao: CartaoControleEdicaoView, historico: readonly CartaoControleEdicaoView[]): FatosCabecalhoCp {
  const situacao = situacaoDaEdicao(edicao);
  return {
    identificador: identificadorCp(edicao.sequencia),
    situacao,
    avisoRascunho: situacao === 'Rascunho' ? AVISO_RASCUNHO_CP : null,
    dataHora: formatarDataHoraOperacional(edicao.createdAt),
    substituidaPor: substituidaPorSequencia(edicao, historico),
  };
}

/** Ordem estável do histórico: sequência crescente, desempate por id. */
export function ordenarHistoricoCp(historico: readonly CartaoControleEdicaoView[]): readonly CartaoControleEdicaoView[] {
  return [...historico].sort((a, b) => a.sequencia - b.sequencia || a.id.localeCompare(b.id));
}

/** A prévia mostra a edição selecionada; sem seleção (ou seleção inexistente), a atual. */
export function edicaoVisualizada(historico: readonly CartaoControleEdicaoView[], selecionadaId: string | null, atual: CartaoControleEdicaoView | null): CartaoControleEdicaoView | null {
  return (selecionadaId ? historico.find((item) => item.id === selecionadaId) : undefined) ?? atual;
}
